import { type OverviewDatum } from '@resistapp/client/data-utils/plot-data/build-overview-line-data';
import { FullSample, getOriginalEnvironmentId } from '@resistapp/common/types';
import { chain } from 'lodash';
import { useMemo } from 'react';
import { useSampleDataContext } from '../../sample-data-context';

interface UseSingleSiteSelectedProps {
  supportedSamples: FullSample[] | undefined;
  mapData: OverviewDatum[] | undefined;
}

export function useSelectedSiteDatum({
  supportedSamples,
  mapData,
}: UseSingleSiteSelectedProps): OverviewDatum | undefined {
  const { queryFilters } = useSampleDataContext();

  const { selectedEnvironmentIdsOrdered } = queryFilters.filters;
  const selectedSiteDatum = useMemo(() => {
    if (mapData && mapData.length === 1) {
      return mapData[0];
    }
    // Find samples with selected environment IDs and group by location
    const uniqSelectedLocations = chain(supportedSamples)
      .filter(s => {
        const envId = getOriginalEnvironmentId(s.environment) || s.environment.id;
        return selectedEnvironmentIdsOrdered.includes(envId);
      })
      .map(s => `${s.environment.lat},${s.environment.lon}`)
      .uniq()
      .value();

    // Single site is selected if we have 1 environment selected or 2 environments at the same location
    const singleSiteSelected =
      selectedEnvironmentIdsOrdered.length === 1 ||
      (selectedEnvironmentIdsOrdered.length === 2 && uniqSelectedLocations.length === 1);

    // If single site selected, use the environment IDs for filtering map data
    const selectedEnvIds = singleSiteSelected ? selectedEnvironmentIdsOrdered : [];

    return selectedEnvIds.length
      ? mapData?.filter(d => {
          // Normally, we have both the before and after enviornments in selectedEnvironmentIdsOrdered,
          // but the implicit population of selectedEnvironmentIdsOrdered
          // for a single process-site project with a treated WW selected
          // only ends up selecting one of the site environments.
          const primaryEnvId = getOriginalEnvironmentId(d.environment) || d.environment.id;
          const pairedEnvId = d.environmentAfter
            ? getOriginalEnvironmentId(d.environmentAfter) || d.environmentAfter.id
            : undefined;
          return selectedEnvIds.includes(primaryEnvId) || (!!pairedEnvId && selectedEnvIds.includes(pairedEnvId));
        })[0]
      : undefined;
  }, [mapData, supportedSamples, selectedEnvironmentIdsOrdered]);

  return selectedSiteDatum;
}
